import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField,
  Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

const DEFAULT_LOCATION = 'Ardent Academy, 820 Roosevelt, Irvine, CA 92620'

type MeetingKind   = 'group' | 'office_hours'
type MeetingFormat = 'in_person' | 'online' | 'simulcast'

export interface MeetingFormState {
  meeting_id?:    string         // present in edit mode
  kind:           MeetingKind
  scheduled_at:   string         // ISO
  duration_min:   number
  format:         MeetingFormat
  location:       string
  zoom_id?:       string
  zoom_passcode?: string
  zoom_url?:      string
  recording_url?: string
  host_mentor_id?: string
  notes?:         string
}

interface MentorOption {
  user_id:    string
  first_name: string
  last_name:  string
}

interface Props {
  open:       boolean
  classId:    string
  /** When set: edit existing meeting OR copy (with no meeting_id). */
  initial?:   Partial<MeetingFormState>
  /** Edit mode if true; create or copy if false. */
  editMode:   boolean
  /** Mentor list for the host dropdown (class_mentor pool). */
  mentors:    MentorOption[]
  onClose:    () => void
  onSaved:    () => void
}

export default function ClassMeetingDialog({
  open, classId, initial, editMode, mentors, onClose, onSaved,
}: Props) {
  const [kind,         setKind]         = useState<MeetingKind>('group')
  const [date,         setDate]         = useState('')   // YYYY-MM-DD (local)
  const [time,         setTime]         = useState('18:00')
  const [duration,     setDuration]     = useState(60)
  const [format,       setFormat]       = useState<MeetingFormat>('in_person')
  const [zoomId,       setZoomId]       = useState('')
  const [zoomPass,     setZoomPass]     = useState('')
  const [recordingUrl, setRecordingUrl] = useState('')
  const [hostId,       setHostId]       = useState('')
  const [notes,        setNotes]        = useState('')
  const [repeatNext4,  setRepeatNext4]  = useState(false)
  const [submitting,   setSubmitting]   = useState(false)
  const [error,        setError]        = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    if (initial) {
      setKind(initial.kind ?? 'group')
      if (initial.scheduled_at) {
        const d = new Date(initial.scheduled_at)
        setDate(d.toISOString().slice(0, 10))
        setTime(d.toTimeString().slice(0, 5))
      } else {
        setDate('')
        setTime('18:00')
      }
      setDuration(initial.duration_min ?? 60)
      setFormat(initial.format ?? 'in_person')
      setZoomId(initial.zoom_id ?? '')
      setZoomPass(initial.zoom_passcode ?? '')
      setRecordingUrl(initial.recording_url ?? '')
      setHostId(initial.host_mentor_id ?? '')
      setNotes(initial.notes ?? '')
      setRepeatNext4(false)
    } else {
      setKind('group')
      setDate(''); setTime('18:00'); setDuration(60)
      setFormat('in_person')
      setZoomId(''); setZoomPass('')
      setRecordingUrl('')
      setHostId('')
      setNotes('')
      setRepeatNext4(false)
    }
  }, [open, initial])

  function buildScheduledAt(): string | null {
    if (!date) return null
    return new Date(`${date}T${time}`).toISOString()
  }

  async function handleSave() {
    setError(null)
    const scheduled_at = buildScheduledAt()
    if (!scheduled_at) { setError('Date is required.'); return }
    if (kind === 'office_hours' && !hostId) {
      setError('Office hours require a host mentor.'); return
    }

    // MVP: location is the fixed Ardent Academy address for in-person/simulcast.
    // Zoom URL is no longer captured — students join via Meeting ID + Passcode.
    const submitLocation = (format === 'in_person' || format === 'simulcast') ? DEFAULT_LOCATION : null

    setSubmitting(true)

    if (editMode && initial?.meeting_id) {
      const { data, error: invokeErr } = await supabase.functions.invoke('update-class-meeting', {
        body: {
          meeting_id:     initial.meeting_id,
          scheduled_at,
          duration_min:   duration,
          format,
          location:       submitLocation,
          zoom_id:        zoomId.trim() || null,
          zoom_passcode:  zoomPass.trim() || null,
          zoom_url:       null,
          recording_url:  recordingUrl.trim() || null,
          host_mentor_id: hostId || null,
          notes:          notes.trim() || null,
        },
      })
      setSubmitting(false)
      if (invokeErr) { setError(await readFunctionError(invokeErr)); return }
      if (data?.error) { setError(data.error); return }
    } else {
      const { data, error: invokeErr } = await supabase.functions.invoke('create-class-meeting', {
        body: {
          class_id:       classId,
          kind,
          scheduled_at,
          duration_min:   duration,
          format,
          location:       submitLocation,
          zoom_id:        zoomId.trim() || null,
          zoom_passcode:  zoomPass.trim() || null,
          zoom_url:       null,
          recording_url:  recordingUrl.trim() || null,
          host_mentor_id: hostId || null,
          notes:          notes.trim() || null,
          repeat_weekly_count: repeatNext4 ? 4 : 0,
        },
      })
      setSubmitting(false)
      if (invokeErr) { setError(await readFunctionError(invokeErr)); return }
      if (data?.error) { setError(data.error); return }
    }
    onSaved()
    onClose()
  }

  const showZoom = format === 'online' || format === 'simulcast'
  const showLocation = format === 'in_person' || format === 'simulcast'

  const title = editMode
    ? 'Edit Meeting'
    : initial?.meeting_id === undefined && initial
      ? 'Copy Meeting'
      : 'Add Meeting'

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl size="small" fullWidth required disabled={editMode}>
            <InputLabel>Kind</InputLabel>
            <Select label="Kind" value={kind} onChange={e => setKind(e.target.value as MeetingKind)}>
              <MenuItem value="group">Group meeting</MenuItem>
              <MenuItem value="office_hours">Office hours</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date" size="small" type="date" required
              value={date} onChange={e => setDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Time (PT)" size="small" type="time" required
              value={time} onChange={e => setTime(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Duration (min)" size="small" type="number"
              value={duration} onChange={e => setDuration(Math.max(15, Number(e.target.value) || 60))}
              sx={{ width: 130 }}
            />
          </Box>

          <FormControl size="small" fullWidth required>
            <InputLabel>Format</InputLabel>
            <Select label="Format" value={format} onChange={e => setFormat(e.target.value as MeetingFormat)}>
              <MenuItem value="in_person">In-person (Ardent Academy)</MenuItem>
              <MenuItem value="online">Online (Zoom)</MenuItem>
              <MenuItem value="simulcast">Simulcast (both)</MenuItem>
            </Select>
          </FormControl>

          {showLocation && (
            <TextField
              label="In-person location" size="small" fullWidth
              value={DEFAULT_LOCATION}
              slotProps={{ input: { readOnly: true } }}
              helperText="Standard Ardent Academy address (read-only for MVP)"
            />
          )}

          {showZoom && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Meeting ID" size="small"
                value={zoomId} onChange={e => setZoomId(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Passcode" size="small"
                value={zoomPass} onChange={e => setZoomPass(e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
          )}

          <FormControl size="small" fullWidth required={kind === 'office_hours'}>
            <InputLabel>Host mentor{kind === 'group' ? ' (optional, defaults to class primary)' : ''}</InputLabel>
            <Select
              label={`Host mentor${kind === 'group' ? ' (optional, defaults to class primary)' : ''}`}
              value={hostId} onChange={e => setHostId(e.target.value)}
            >
              {kind === 'group' && <MenuItem value="">(use class primary mentor)</MenuItem>}
              {mentors.map(m => (
                <MenuItem key={m.user_id} value={m.user_id}>
                  {`${m.first_name} ${m.last_name}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Notes (agenda, optional)"
            size="small" fullWidth multiline rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
          />

          <TextField
            label="Recording URL (optional)"
            size="small" fullWidth type="url"
            placeholder="https://zoom.us/rec/share/…"
            value={recordingUrl} onChange={e => setRecordingUrl(e.target.value)}
            helperText="Zoom cloud recording link — students can review it after class."
          />

          {!editMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={repeatNext4}
                  onChange={e => setRepeatNext4(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Also create the next 4 weekly meetings (5 total)
                </Typography>
              }
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : (editMode ? 'Save' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
